import { useContext, useState } from "react";
import {
  PersonFiltersContext,
  PersonFiltersEventContext,
} from "../../../../context/FiltersContext";
import {
  IPersonForeningFilterSetting,
  QueryFilter,
  PersonFilterKeys,
} from "../../../../interfaces/filters";
import { AccordionFilterItem } from "../../../accordion/Accordion";
import AutoCompleteForening from "../../../autocomplete/AutoCompleteForening";
import AppliedFilterItem from "../../../filter/AppliedFilterItem";
import { f } from "../../../../util/f";
import {
  PersonDtoFilterInput,
  PersonForeningFilterInput,
} from "../../../../interfaces/schema";
import { DateFilterEvent } from "../../../filter/date/types";
import DateFilter from "../../../filter/date/DateFilter";
import { defaultDateEvent } from "../../../filter/date/dateFilterHelpers";

// helpers
const filterKey: QueryFilter<PersonFilterKeys>["filterKey"] = "personForening";

export default function PersonForeningFilter() {
  const filters = useContext(PersonFiltersContext);
  const updateFilters = useContext(PersonFiltersEventContext);

  // find active settings
  const settings: IPersonForeningFilterSetting = filters.find(
    (f) => f.filterKey === filterKey,
  )?.settings;

  // state
  const [latestDateEvent, setLatestDateEvent] =
    useState<DateFilterEvent>(defaultDateEvent);

  // stuff
  const isDateDisabled = Boolean(!settings?.activeFilters?.length);

  // handlers
  function updateFilterHandler(
    selectedItem: IForening | null,
    dateFilterEvent?: DateFilterEvent,
  ) {
    // get active list if any otherwise initialise an empty array
    const activeFilters = settings?.activeFilters || [];

    // add selected item if it does not exist in active list
    if (selectedItem && !activeFilters.some((s) => s.id === selectedItem.id)) {
      activeFilters.push({
        id: selectedItem.id,
        name: selectedItem.navn,
      });
    }

    // check for valid date event otherwise assign latest date event
    if (!dateFilterEvent) {
      dateFilterEvent = latestDateEvent;
    } else {
      setLatestDateEvent(dateFilterEvent);
    }

    const filters: PersonForeningFilterInput[] = activeFilters.map((set) => {
      return {
        and: [
          {
            foreningId: {
              eq: set.id,
            },
          },
          {
            periode: dateFilterEvent?.filter,
          },
        ],
      };
    });

    // build the filter string
    const filterString = f<PersonDtoFilterInput>()
      .addFilter("foreningPerson", {
        some: {
          or: filters,
        },
      })
      .build();

    const updatedSettings: IPersonForeningFilterSetting = {
      activeFilters: activeFilters,
      dateFilterSettings: dateFilterEvent.settings,
    };

    const queryFilter: QueryFilter<
      PersonFilterKeys,
      IPersonForeningFilterSetting
    > = {
      filter: filterString,
      filterKey: filterKey,
      settings: updatedSettings,
      parentFilter: "and",
    };

    updateFilters({
      event: "update",
      queryFilter: queryFilter,
    });
  }

  function removeFilterHandler(
    id: IPersonForeningFilterSetting["activeFilters"][number]["id"],
  ) {
    const updatedFilters =
      settings?.activeFilters?.filter((set) => {
        return id !== set.id;
      }) || [];

    const filters: PersonForeningFilterInput[] = updatedFilters.map((set) => {
      return {
        and: [
          {
            foreningId: {
              eq: set.id,
            },
          },
          {
            periode: latestDateEvent?.filter,
          },
        ],
      };
    });

    // build the filter string
    const filterString = f<PersonDtoFilterInput>()
      .addFilter("foreningPerson", {
        some: {
          or: filters,
        },
      })
      .build();

    const updatedSettings: IPersonForeningFilterSetting = {
      activeFilters: updatedFilters,
      dateFilterSettings: latestDateEvent.settings,
    };

    const update = updatedSettings?.activeFilters?.length > 0;

    if (update) {
      updateFilters({
        event: "update",
        queryFilter: {
          filterKey: filterKey,
          filter: filterString,
          settings: updatedSettings,
        },
      });
    } else {
      updateFilters({
        event: "delete",
        queryFilter: {
          filterKey: filterKey,
        },
      });
    }
  }

  return (
    <AccordionFilterItem
      title="Forening"
      value="forening"
      isActive={filters.some((f) => f.filterKey === filterKey)}
    >
      {settings && (
        <div className="c-applied-filter-group">
          {settings?.activeFilters?.map((filter) => (
            <AppliedFilterItem
              key={filter.id}
              label={filter.name}
              removeFilterHandler={() => removeFilterHandler(filter.id)}
            />
          ))}
        </div>
      )}
      <AutoCompleteForening
        placeholder="Vælg forening ..."
        clearAfterSelection={true}
        onSelectedItemChange={(selectedItem) => {
          updateFilterHandler(selectedItem);
        }}
      />
      <div className="mt-3">
        <DateFilter
          handleChange={(dateFilterEvent) => {
            updateFilterHandler(null, dateFilterEvent);
          }}
          settings={settings?.dateFilterSettings}
          disabled={isDateDisabled}
          key={JSON.stringify(settings?.dateFilterSettings)}
        />
      </div>
    </AccordionFilterItem>
  );
}
